import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import Users from "./Users";

const TABS = [
  { key: "vendors", label: "Vendors", perm: "edit" },
  { key: "pops", label: "POPs", perm: "edit" },
  { key: "aisync", label: "AI & Sync", perm: "manage_invoices" },
  { key: "users", label: "Users & Roles", perm: "manage_users" },
];

export default function Settings() {
  const { can } = useAuth();
  const avail = TABS.filter((t) => can(t.perm));
  const [tab, setTab] = useState(avail[0]?.key || "vendors");

  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-extrabold">Settings</h1>
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {avail.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === t.key ? "bg-white shadow-sm" : "text-slate-500"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "users" ? <Users /> : tab === "aisync" ? <AiSync /> : <SimpleMaster entity={tab} />}
    </div>
  );
}

function AiSync() {
  const [cfg, setCfg] = useState(null);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoConfirm, setAutoConfirm] = useState(false);
  const load = useCallback(async () => {
    try {
      const [c, s, a] = await Promise.all([
        api.get("/vendor-invoices/ai/config"),
        api.get("/sync/lastmile/report"),
        api.get("/vendor-invoices/ai/settings"),
      ]);
      setCfg(c.data); setSync(s.data); setAutoConfirm(!!a.data.auto_confirm);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not load"); }
  }, []);
  const toggleAutoConfirm = async () => {
    try { const { data } = await api.put("/vendor-invoices/ai/settings", { auto_confirm: !autoConfirm });
      setAutoConfirm(!!data.auto_confirm); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save"); }
  };
  useEffect(() => { load(); }, [load]);
  const syncNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/sync/lastmile"); toast.success(`Sync ${data.ok ? "OK" : "failed"}: created ${data.created}, updated ${data.updated}`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Sync failed"); } finally { setBusy(false); }
  };
  const cap = cfg?.cap || {};
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 max-w-xl">
      <div>
        <h3 className="font-head font-bold text-sm text-slate-800">AI (Claude {cfg?.model || "…"})</h3>
        <div className="mt-2 text-sm text-slate-700 space-y-1">
          <div>Monthly cap: <b>₹{cfg?.monthly_cap_inr}</b> · spent <b>₹{cap.spent_inr ?? "…"}</b> ({cap.cap_inr ? Math.round((cap.spent_inr / cap.cap_inr) * 100) : 0}%)</div>
          <div>Max parallel reads: <b>{cfg?.max_parallel}</b> <span className="text-xs text-slate-400">(set via AI_MAX_PARALLEL / cap via AI_MONTHLY_CAP_INR in .env, then restart)</span></div>
        </div>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <h3 className="font-head font-bold text-sm text-slate-800">AI decisions</h3>
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={autoConfirm} onChange={toggleAutoConfirm} />
          Auto-confirm bills the AI is confident about
        </label>
        <div className="text-xs text-slate-400 mt-1">Default OFF. A person always clicks Confirm — nothing is matched or paid on AI's word alone. When the AI is unsure it asks a specific question instead.</div>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <h3 className="font-head font-bold text-sm text-slate-800">LastMile sync</h3>
        <div className="mt-2 text-sm text-slate-700">
          {sync?.ran_at ? <span>Last run {fmtDate(sync.ran_at)} · {sync.ok ? "OK" : "failed"} · created {sync.created}, updated {sync.updated}</span>
            : <span className="text-slate-400">No sync has run yet.</span>}
        </div>
        <button onClick={syncNow} disabled={busy} className="mt-3 px-4 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">{busy ? "Syncing…" : "Sync now"}</button>
        <div className="text-xs text-slate-400 mt-1">Runs automatically every 15 minutes.</div>
      </div>
    </div>
  );
}

const FIELDS = {
  vendors: [["name", "Name*"], ["code", "Vendor Code"], ["contact_person", "Contact Person"], ["accounts_contact", "Accounts Contact"], ["email", "Email"], ["phone", "Phone"], ["payment_method", "Payment Method"]],
  pops: [["name", "Name*"], ["location", "Location*"], ["pop_type", "Type (Fiber/RF/Both)"], ["address", "Address"], ["city", "City"], ["state", "State"]],
};
const COLS = {
  vendors: [["name", "Name"], ["code", "Code"], ["phone", "Phone"]],
  pops: [["internal_pop_id", "POP ID"], ["name", "Name"], ["location", "Location"], ["pop_type", "Type"], ["served_connections", "Serves"], ["at_risk", "Risk"]],
};

function SimpleMaster({ entity }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/${entity}`); setRows(data); }, [entity]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (modal.id) await api.put(`/${entity}/${modal.id}`, payload);
      else await api.post(`/${entity}`, payload);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (r) => { if (!window.confirm("Archive?")) return; try { await api.delete(`/${entity}/${r.id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-3">
      {can("create") && <button onClick={() => setModal({ data: entity === "pops" ? { pop_type: "Fiber" } : {} })} className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2"><Plus size={16} /> Add {entity === "pops" ? "POP" : "Vendor"}</button>}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            {COLS[entity].map((c) => <th key={c[0]} className="px-4 py-3">{c[1]}</th>)}<th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                {COLS[entity].map((c) => <td key={c[0]} className="px-4 py-3">{c[0] === "at_risk" ? (r[c[0]] ? <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">AT RISK</span> : "OK") : (r[c[0]] ?? "—")}</td>)}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {can("edit") && <button onClick={() => setModal({ id: r.id, data: r })} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>}
                    {can("delete") && <button onClick={() => remove(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={COLS[entity].length + 1} className="px-4 py-8 text-center text-slate-400">No records.</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <Form entity={entity} modal={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

// GST state codes (first two digits of a GSTIN) → state, for live display only.
// The backend is the source of truth and re-validates on save.
const GST_STATES = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh",
  "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh",
  "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal",
  "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "25": "Daman & Diu", "26": "Dadra & Nagar Haveli and Daman & Diu", "27": "Maharashtra",
  "28": "Andhra Pradesh (Old)", "29": "Karnataka", "30": "Goa", "31": "Lakshadweep", "32": "Kerala",
  "33": "Tamil Nadu", "34": "Puducherry", "35": "Andaman & Nicobar Islands", "36": "Telangana",
  "37": "Andhra Pradesh", "38": "Ladakh", "97": "Other Territory", "99": "Centre",
};
const gstState = (g) => GST_STATES[(g || "").trim().slice(0, 2)] || "";

function Form({ entity, modal, onClose, onSave }) {
  const [f, setF] = useState(modal.data || {});
  const [gstins, setGstins] = useState(() =>
    (modal.data?.gstins || []).map((g) => (typeof g === "string" ? g : g.gstin)));
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500";
  const setGstin = (i, v) => setGstins((p) => p.map((g, j) => (j === i ? v.toUpperCase() : g)));
  const save = () => {
    const payload = { ...f };
    if (entity === "vendors") payload.gstins = gstins.map((g) => g.trim()).filter(Boolean);
    onSave(payload);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg">{modal.id ? "Edit" : "New"} {entity === "pops" ? "POP" : "Vendor"}</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 grid grid-cols-2 gap-4">
          {FIELDS[entity].map(([k, l]) => (
            <div key={k} className={k === "address" ? "col-span-2" : ""}>
              <label className="text-xs font-semibold text-slate-600">{l}</label>
              <input className={field} value={f[k] ?? ""} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} />
            </div>
          ))}
          {entity === "vendors" && (
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-600">GSTINs <span className="font-normal text-slate-400">(one per state — state detected from each GSTIN)</span></label>
              <div className="mt-1 space-y-2">
                {gstins.map((g, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={`${field} mt-0 font-mono tracking-wide`} maxLength={15} placeholder="e.g. 27AAACB1234C1Z5"
                      value={g} onChange={(e) => setGstin(i, e.target.value)} />
                    <span className="text-xs text-slate-500 w-40 shrink-0">{gstState(g) || (g ? "unknown state code" : "")}</span>
                    <button type="button" onClick={() => setGstins((p) => p.filter((_, j) => j !== i))} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setGstins((p) => [...p, ""])} className="text-sm text-[var(--brand)] font-semibold flex items-center gap-1"><Plus size={14} /> Add GSTIN</button>
              </div>
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-3"><button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={save} className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Save</button></div>
      </div>
    </div>
  );
}
